import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import momentTZ from 'moment-timezone';
import { useAuth } from '../modules/auth';
import { resolveInitialTimezone } from '../modules/widgets/components/timezone/resolveInitialTimezone';

type TimezoneContextValue = {
  tzid: string;
  setTzid: (tz: string) => void;
  userSelected: boolean; // true if user explicitly picked a timezone
};

const TimezoneContext = createContext<TimezoneContextValue | undefined>(undefined);

const SELECTED_TZ_KEY = 'selectedTimezone';

export function TimezoneProvider({ children }: { children: React.ReactNode }) {
  const { currentUser } = useAuth();

  const guessTimeZone = useMemo(() => momentTZ.tz.guess(), []);

  const [tzid, setTzidState] = useState<string>(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem(SELECTED_TZ_KEY) : null;
    return resolveInitialTimezone({ explicit: saved });
  });
  const [userSelected, setUserSelected] = useState<boolean>(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem(SELECTED_TZ_KEY) : null;
    return !!saved;
  });

  const setTzid = useCallback((tz: string) => {
    setTzidState(tz);
    setUserSelected(true);
  }, []);

  // If there is no saved tz, prefer provider timezone once user is loaded
  useEffect(() => {
    if (!currentUser) return;
    const saved = localStorage.getItem(SELECTED_TZ_KEY);
    const providerTz = currentUser?.provider?.timezone;
    if (!saved && providerTz) {
      setTzidState((prev) => (prev === guessTimeZone ? providerTz : prev));
    }
  }, [currentUser, guessTimeZone]);

  // Persist selection only when explicitly selected by user
  useEffect(() => {
    if (!userSelected) return;
    try {
      localStorage.setItem(SELECTED_TZ_KEY, tzid);
    } catch {}
  }, [tzid, userSelected]);

  const value = useMemo(() => ({ tzid, setTzid, userSelected }), [tzid, setTzid, userSelected]);

  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>;
}

export function useTimezone(): TimezoneContextValue {
  const ctx = useContext(TimezoneContext);
  if (!ctx) throw new Error('useTimezone must be used within a TimezoneProvider');
  return ctx;
}


